'use client';

import Link from 'next/link';
import {
  Bell,
  CheckCircle2,
  Clock3,
  Layers3,
  ListPlus,
  MessageCircle,
  Sparkles,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/Card';
import { mainPricingPlan } from '@/content/pricing';
import { cn } from '@/lib/utils';

const includedFeatureDetails: Array<{
  description: string;
  icon: LucideIcon;
  title: string;
}> = [
  {
    description: 'Actívalo si quieres que tus clientes consulten disponibilidad y gestionen citas desde WhatsApp.',
    icon: MessageCircle,
    title: 'Bot de WhatsApp opcional',
  },
  {
    description: 'Envía recordatorios por WhatsApp desde la agenda cuando lo necesites.',
    icon: Bell,
    title: 'Recordatorios desde la agenda',
  },
  {
    description: 'Configura hasta 10 áreas o especialidades para representar mejor todo lo que haces.',
    icon: Layers3,
    title: 'Hasta 10 categorías de actividad',
  },
  {
    description: 'Publica todos los servicios que necesites, sin el límite de 3 del Plan Gratis.',
    icon: ListPlus,
    title: 'Servicios ilimitados',
  },
  {
    description: 'Configura disponibilidad, bloqueos, descansos y ausencias del equipo.',
    icon: Clock3,
    title: 'Manejo de ausencias y horarios',
  },
  {
    description: 'Conserva la agenda, los clientes, las reservas online y el perfil público del plan base.',
    icon: Users,
    title: 'Todo lo incluido en Gratis',
  },
];

function formatPrice(price: string): string {
  return Number(price.replace(/,/g, '')).toLocaleString('es-CO');
}

interface PricingPlanCardProps {
  className?: string;
  showFeatures?: boolean;
}

export function PricingPlanCard({ className, showFeatures = true }: PricingPlanCardProps) {
  const plan = mainPricingPlan;

  return (
    <Card className={cn('relative overflow-hidden border-accent/40 bg-bg-primary/78 p-0 shadow-mockup', className)}>
      <div className="h-1.5 bg-accent" />
      <CardHeader className="gap-6 p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="accent" className="px-4 py-1 text-sm font-semibold">
            Plan Profesional
          </Badge>
          <span className="inline-flex items-center gap-2 rounded-chip border border-border-strong bg-bg-secondary px-3 py-1.5 text-xs font-semibold text-text-secondary">
            <Sparkles size={14} className="text-accent" />
            Sin comisiones por cita
          </span>
        </div>

        <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(20rem,24rem)] xl:items-end">
          <div>
            <CardTitle className="text-3xl sm:text-4xl">{plan.name}</CardTitle>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-text-secondary sm:text-base">
              {plan.description}
            </p>
            <div className="mt-6">
              <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                <span className="font-heading text-5xl font-bold leading-none text-text-primary sm:text-6xl">
                  ${formatPrice(plan.price)}
                </span>
                <span className="pb-1 text-sm font-semibold text-text-secondary sm:text-base">
                  {plan.currency}{plan.period}
                </span>
              </div>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="border-l-2 border-accent/60 pl-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                <UserRound size={16} className="text-accent" />
                {plan.professionals} incluido
              </p>
              <p className="mt-2 text-sm leading-6 text-text-secondary">
                Cada profesional adicional cuesta{' '}
                <span className="font-semibold text-text-primary">
                  ${formatPrice(plan.extraProfessionalPrice)} COP/mes
                </span>.
              </p>
            </div>

            <div className="border-l-2 border-accent/60 pl-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-text-primary">
                <MessageCircle size={16} className="text-accent" />
                Bot de WhatsApp incluido
              </p>
              <p className="mt-2 text-sm leading-6 text-text-secondary">
                Su activación es opcional: puedes usar OficioYa sin conectarlo.
              </p>
            </div>
          </div>
        </div>
      </CardHeader>

      {showFeatures ? (
        <CardContent className="p-6 pt-0 sm:p-8 sm:pt-0">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-accent">
            También incluye
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {plan.features.map((feature) => (
              <li key={feature} className="flex items-start gap-3 rounded-lg border border-border bg-bg-secondary/70 px-4 py-3 text-sm text-text-primary">
                <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-success" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      ) : null}

      <CardFooter className="flex flex-col gap-3 p-6 pt-0 sm:p-8 sm:pt-0">
        <Button size="lg" className="w-full" asChild>
          <Link
            href="/registro?plan=PROFESIONAL"
            data-track-event="pricing_cta_click"
            data-pricing-cta="true"
            data-pricing-plan="Profesional mensual"
            data-cta-location="pricing"
            data-cta-text={plan.ctaText}
          >
            {plan.ctaText}
          </Link>
        </Button>
        <p className="text-center text-xs text-text-secondary">
          Crea tu negocio y paga de forma segura con Mercado Pago.
        </p>
      </CardFooter>
    </Card>
  );
}

interface PricingFeatureGridProps {
  className?: string;
}

export function PricingFeatureGrid({ className }: PricingFeatureGridProps) {
  return (
    <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {includedFeatureDetails.map((feature) => {
        const FeatureIcon = feature.icon;

        return (
          <article key={feature.title} className="rounded-card border border-border bg-bg-secondary p-5">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-accent/10 text-accent">
              <FeatureIcon size={21} />
            </div>
            <h3 className="font-heading text-lg font-semibold text-text-primary">{feature.title}</h3>
            <p className="mt-2 text-sm leading-6 text-text-secondary">{feature.description}</p>
          </article>
        );
      })}
    </div>
  );
}
